<!--src/components/nav/NavBox.vue-->
<script setup>
import { onMounted, ref, watch, computed } from 'vue'
import { useLocationStore } from '@/store/location'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/modules/userStore'; // 导入用户仓库
import { storeToRefs } from 'pinia'; // 响应式解构 Pinia 状态
import { logout } from "@/api/user";
import { ElMessage } from 'element-plus';

const isNavOpen = ref(false)
const locationStore = useLocationStore()
const router = useRouter()
const userStore = useUserStore();
const { isLogin, userInfo } = storeToRefs(userStore);

// 控制用户头像hover弹窗的显示/隐藏+弹窗坐标（解决位置修改不生效）
const avatarPopupShow = ref(false)
const popupPosition = ref({ top: 0, left: 0 }) // 弹窗固定定位坐标
const popupOffsetX = ref(0) // 弹窗相对于头像的X偏移（可修改，立即生效）
const popupOffsetY = ref(0) // 弹窗相对于头像的Y偏移（可修改，立即生效）

// 【核心复用】从AvatarEdit.vue复制：解析前端预存头像路径
const getAvatarUrlById = (avatarId) => {
  if (avatarId && avatarId.startsWith('http')) {
    return avatarId;
  }
  if (!avatarId) return 'https://picsum.photos/seed/default-avatar/200/200';

  const [category, indexStr] = avatarId.split('_');
  if (!category || !indexStr) return 'https://picsum.photos/seed/default-avatar/200/200';

  const index = indexStr.replace(/^0+/, '') || indexStr;

  try {
    // 完全复用AvatarEdit.vue的资源路径，确保前端预存头像正确加载
    return require(`@/assets/avatars/${category}/${index}.png`);
  } catch (err) {
    console.warn('【NavBox】生成前端头像URL失败：', err, '头像ID：', avatarId);
    return 'https://picsum.photos/seed/default-avatar/200/200';
  }
};

// 计算属性：缓存有效用户信息，复用前端头像解析逻辑
const validUserInfo = computed(() => {
  if (!userInfo.value || !isLogin.value) return {
    avatar: getAvatarUrlById(''), // 兜底调用统一方法
    username: '未知用户',
    phone: '未绑定手机号'
  };

  // 1. 提取仓库中的头像ID（如male_01），调用统一方法解析成本地路径
  const avatarId = userInfo.value.avatar || userInfo.value.avatarUrl || '';
  const validAvatar = getAvatarUrlById(avatarId); // 关键：复用前端解析逻辑，不再拼接后端路径

  // 2. 字段映射适配，确保数据准确
  const validUsername = userInfo.value.username || userInfo.value.nickname || '未知用户';
  const validPhone = userInfo.value.phone || userInfo.value.mobile || '未绑定手机号';

  return {
    avatar: validAvatar,
    username: validUsername,
    phone: validPhone
  };
})

const toggleNavList = () => {
  isNavOpen.value = !isNavOpen.value
}

watch(isNavOpen, (newVal) => {
  const button = document.getElementById('headerNavToggler')
  if (button) {
    button.setAttribute('aria-expanded', newVal)
  }
})

const goToProfile = (e) => {
  e.preventDefault()
  e.stopPropagation()
  avatarPopupShow.value = false; // 跳转个人中心时关闭弹窗
  router.push('/PersonalCenter')
}

// 处理退出登录逻辑（调用后端接口+用户仓库重置状态）
const handleLogout = async (e) => {
  e.preventDefault()
  e.stopPropagation()
  try {
    // 1. 调用后端退出接口
    await logout();
    // 2. 调用用户仓库的退出方法，重置所有状态
    await userStore.handleLogout();
    // 3. 关闭弹窗，给出成功提示
    avatarPopupShow.value = false;
    ElMessage.success('退出登录成功');
    // 4. 关闭移动端导航列表（若打开）
    isNavOpen.value = false;
  } catch (error) {
    console.error('退出登录失败：', error);
    ElMessage.error('退出登录失败，请稍后重试');
  }
}

// 阻止弹窗事件冒泡（确保弹窗内按钮可点击，不被父容器拦截）
const stopPopupPropagation = (e) => {
  e.stopPropagation();
}

// 【核心优化】获取头像坐标，设置弹窗固定定位（解决位置修改不生效）
const setPopupPosition = (e) => {
  if (!e.target) return;
  const avatarRect = e.target.getBoundingClientRect(); // 获取头像相对于视口的坐标
  // 计算弹窗坐标：头像右侧+偏移X，头像下方+偏移Y（可修改offsetX/offsetY调整位置）
  popupPosition.value = {
    top: avatarRect.bottom + popupOffsetY.value,
    left: avatarRect.right + popupOffsetX.value
  };
  avatarPopupShow.value = true; // 显示弹窗
}

// 【核心优化】弹窗自身控制隐藏，鼠标移出弹窗才消失（彻底解决无法点击问题）
const hidePopupOnlyWhenLeave = () => {
  avatarPopupShow.value = false;
}

onMounted(async () => {
  await locationStore.fetchIPLocation()
  const button = document.getElementById('headerNavToggler')
  if (button) {
    button.setAttribute('aria-expanded', isNavOpen.value)
  }
  // 原有逻辑：初始化用户状态，确保数据拉取完整
  await userStore.initUserStatus();
})
</script>

<template>
  <div
    class="nav-box container-fluid px-0 m-0"
    style="overflow: visible; height: 30px;"
  >
    <div
      class="nav-content container d-flex align-items-center m-auto navbar"
      style="height: 100%; width: 100%"
    >
      <div
        class="nav-left d-flex align-items-center justify-content-start flex-grow-1"
        style="height: 100%;"
      >
        <ul
          class="header-user-menu d-flex align-items-center m-0 p-0 list-unstyled d-lg-flex d-none"
          style="height: 100%;"
        >
          <li
            class="location-item me-2 d-flex align-items-center"
            style="height: 100%; font-size: 14px;"
          >
            <i
              class="iconfont icon-dizhi me-1"
            />
            {{
              locationStore.status === 'loading' ? '定位中...' :
              locationStore.status === 'fail' ? '定位失败' :
              locationStore.area || '地区'
            }}
          </li>
          <li
            class="shu me-2 d-flex align-items-center"
            style="height: 100%; font-size: 14px;"
          >
            |
          </li>
          <li
            v-if="!isLogin"
            class="login-item d-flex align-items-center"
            style="height: 100%;"
          >
            <router-link
              to="/login"
              class="login-link text-white text-decoration-none"
              style="font-size: 14px;"
            >
              登录
            </router-link>
          </li>
          <!-- 【核心优化】简化逻辑，头像控制弹窗显示，弹窗自身控制隐藏 -->
          <li
            v-else
            class="user-avatar-wrapper d-flex align-items-center position-relative cursor-pointer"
            style="height: 100%;"
          >
            <div
              class="user-avatar-item d-flex align-items-center"
              style="height: 100%;"
              @click="goToProfile($event)"
              @mouseover="setPopupPosition($event)"
            >
              <!-- 头像：使用统一前端解析方法，显示完美 -->
              <img
                ref="avatarRef"
                :src="validUserInfo.avatar"
                alt="用户头像"
                class="user-avatar"
                :title="validUserInfo.username"
                style="width: 24px; height: 24px; border-radius: 50%; border: 2px solid #ffffff; object-fit: cover; box-shadow: 0 0 2px rgba(0,0,0,0.2);"
                @error="(e) => e.target.src = getAvatarUrlById('')"
              >
              <!-- 用户名 + 下拉箭头，扩大悬停区域 -->
              <span
                class="username-text ms-1"
                style="font-size: 13px; color: #fff; white-space: nowrap;"
              >
                {{ validUserInfo.username }}
              </span>
              <!-- 小箭头 -->
              <svg
                t="1783325691634"
                class="icon"
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                p-id="1911"
                style="width: 16px; height: 16px; margin-left: 5px; vertical-align: middle;"
              >
                <path
                  d="M512 581.5l273.1-273.1c25-25 65.5-25 90.5 0s25 65.5 0 90.5l-316.7 316.7c-12.9 12.9-30 19.2-47 18.7-16.9 0.4-33.9-5.8-46.8-18.699l-316.8-316.8c-25-25-25-65.5 0-90.5s65.5-25 90.5 0l273.2 273.2z"
                  fill="#ffffff"
                  p-id="1912"
                />
              </svg>
            </div>

            <!-- 【核心优化】fixed定位，脱离父容器，位置修改立即生效，自身控制隐藏 -->
            <div
              v-show="avatarPopupShow"
              class="user-avatar-popup"
              :style="{
                position: 'fixed',
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                zIndex: 9999999,
                pointerEvents: 'auto' // 强制开启鼠标交互，确保可点击
              }"
              @click="stopPopupPropagation($event)"
              @mouseleave="hidePopupOnlyWhenLeave"
            >
              <div
                class="popup-content"
                style="background: #faf3fc; border-radius: 8px; box-shadow: 0 2px 16px rgba(0, 0, 0, 0.15); width: 200px; box-sizing: border-box;"
              >
                <!-- 弹窗头部：居中显示，紧凑布局 -->
                <div class="popup-header d-flex flex-column align-items-center justify-content-center p-2 border-b border-gray-200">
                  <img
                    :src="validUserInfo.avatar"
                    alt="用户头像"
                    style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover; margin-bottom: 6px;"
                    @error="(e) => e.target.src = getAvatarUrlById('')"
                  >
                  <div class="popup-user-info text-center">
                    <div
                      class="popup-username fw-bold"
                      style="font-size: 14px;"
                    >
                      {{ validUserInfo.username }}
                    </div>
                    <div
                      class="popup-phone text-sm text-gray-500"
                      style="font-size: 11px;"
                    >
                      {{ validUserInfo.phone }}
                    </div>
                  </div>
                </div>

                <!-- 弹窗主体：功能按钮，紧凑布局 -->
                <div class="popup-body p-2 d-flex flex-column align-items-center">
                  <button
                    class="popup-btn go-center w-100 text-center py-1.5 px-2 mb-1"
                    style="max-width: 180px; font-size: 13px;"
                    @click="goToProfile($event)"
                  >
                    <i
                      class="bi bi-person me-1"
                      style="font-size: 12px;"
                    /> 个人中心
                  </button>
                  <button
                    class="popup-btn logout w-100 text-center py-1.5 px-2"
                    style="max-width: 180px; font-size: 13px;"
                    @click="handleLogout($event)"
                  >
                    <i
                      class="bi bi-box-arrow-right me-1"
                      style="font-size: 12px;"
                    /> 退出登录
                  </button>
                </div>
              </div>
              <!-- 弹窗箭头：可调整位置，与弹窗无缝衔接 -->
              <div
                class="popup-arrow"
                style="position: absolute; top: 0; left: 0; border-width: 4px 4px 4px 0; border-style: solid; border-color: transparent #faf3fc transparent transparent; pointer-events: none;"
              />
            </div>
          </li>
        </ul>

        <!-- 小屏登录/头像：保持已修复逻辑，复用前端头像解析 -->
        <div
          class="mobile-login-btn d-lg-none d-flex align-items-center justify-content-center"
          style="
          width: 30px;
          height: 30px;
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
        "
        >
          <button
            class="btn p-0 border-0 bg-transparent"
            type="button"
            style="outline: none; box-shadow: none;"
            @click="isLogin ? goToProfile($event) : ''"
          >
            <router-link
              v-if="!isLogin"
              to="/login"
              class="d-flex align-items-center justify-content-center w-100 h-100"
            >
              <i
                class="bi bi-person-circle"
                style="font-size: 1.3rem;"
              />
            </router-link>
            <img
              v-else
              :src="validUserInfo.avatar"
              alt="用户头像"
              :title="validUserInfo.username"
              style="
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #ffffff;
          object-fit: cover;
          display: block;
          box-shadow: 0 0 2px rgba(0,0,0,0.2);
          "
              @error="(e) => e.target.src = getAvatarUrlById('')"
            >
          </button>
        </div>
      </div>

      <div
        class="nav-middle d-lg-none d-flex align-items-center justify-content-center"
        style="
        height: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        z-index: 1;
      "
      >
        <span
          class="fs-7"
          style="font-size: 14px; color: #fff;"
        >不折不购_专注省钱</span>
      </div>

      <div
        class="nav-right d-flex align-items-center justify-content-end flex-grow-1"
        style="height: 100%;"
      >
        <button
          id="headerNavToggler"
          class="navbar-toggler d-lg-none border-0 bg-transparent"
          type="button"
          aria-expanded="false"
          aria-label="导航菜单"
          style="position: relative; z-index: 10000; margin-right: 5px; outline: none; box-shadow: none;"
          @click="toggleNavList"
        >
          <span
            v-if="!isNavOpen"
            class="bi bi-grid-3x3-gap-fill fs-4"
            style="font-size: 1.2rem;"
          />
          <span
            v-else
            class="bi bi-x fs-4"
            style="font-size: 1.2rem;"
          />
        </button>

        <ul
          class="header-list d-lg-flex gap-4 m-0 p-0 list-unstyled"
          :class="{
            'd-none': !isNavOpen,
            'd-block': isNavOpen,
            'show': isNavOpen
          }"
          style="
            margin: 0;
            padding: 0;
            list-style: none;
            z-index: 99999;
          "
        >
          <li
            class="list-item"
            style="list-style: none;"
          >
            <router-link
              to="/MyOrder"
              class="order-link text-white text-decoration-none"
              style="display: block; padding: 0.5rem 0; color: #fff; font-size: 14px;"
              @click.stop="toggleNavList"
            >
              我的订单
            </router-link>
          </li>
          <!-- 返现功能暂未上线，先隐藏 -->
          <!-- <li style="list-style: none;">
            <router-link
              to="/MyBill"
              class="bill-link text-white text-decoration-none"
              style="display: block; padding: 0.5rem 0; color: #fff; font-size: 14px;"
              @click="toggleNavList"
            >
              我的返现
            </router-link>
          </li> -->
          <li style="list-style: none;">
            <router-link
              to="/GetCoupons"
              class="coupons-link text-white text-decoration-none"
              style="display: block; padding: 0.5rem 0; color: #fff; font-size: 14px;"
              @click="toggleNavList"
            >
              领券通道
            </router-link>
          </li>
          <li style="list-style: none;">
            <router-link
              to="/favorite"
              class="phone-link text-white text-decoration-none"
              style="display: block; padding: 0.5rem 0; color: #fff; font-size: 14px;"
              @click="toggleNavList"
            >
              我的收藏
            </router-link>
          </li>
          <li style="list-style: none;">
            <router-link
              to="/Strategy"
              class="strategy-link text-decoration-none"
              style="display: block; padding: 0.5rem 0; color: #fff; font-size: 14px;"
              @click="toggleNavList"
            >
              攻略中心
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/css/mixin.scss";
.nav-box {
  position: relative;
  height: 30px !important;
  @include bg_color;
  z-index: 99999 !important;
  width: 100%;
  overflow: visible !important;

  // 原有样式：保留
  .header-user-menu .user-avatar-item:hover .username-tooltip {
    opacity: 1 !important;
    visibility: visible !important;
  }

  .nav-left, .nav-right {
    @include font_color;

    .header-user-menu li, .iconfont, .shu, .login-link {
      @include font_color;
    }

    .header-list .order-link, .bill-link, .coupons-link, .phone-link, .strategy-link {
      @include font_color;
    }
  }

  // 【核心优化】弹窗样式：fixed定位，无父容器约束，可自由调整位置
  .header-user-menu .user-avatar-wrapper .user-avatar-popup {
    // 确保弹窗可交互，优先级最高
    pointer-events: auto !important;
    z-index: 9999999 !important;

    .popup-content {
      color: #333333;

      .popup-header {
        .popup-user-info {
          .popup-username {
            color: #333;
          }

          .popup-phone {
            color: #666;
          }
        }
      }

      .popup-body {
        .popup-btn {
          border: none;
          background: transparent;
          border-radius: 4px;
          width: 100%;
          cursor: pointer;
          color: #333;

          &:hover {
            background: #f0e6f8; // 适配#faf3fc的hover色，更协调
          }

          &.logout {
            color: #ff4d4f;
          }
        }
      }
    }

    .popup-arrow {
      // 箭头样式固定，可通过修改弹窗left调整箭头位置
      pointer-events: none !important;
    }
  }

  // 父容器样式：仅用于承载头像，不约束弹窗
  .header-user-menu .user-avatar-wrapper {
    padding: 0 5px;
    overflow: visible !important;
  }

  @media (max-width: 991.98px) {
    .mobile-login-btn .btn .bi {
      @include font_color;
    }

    .nav-middle span {
      @include font_color;
    }

    .navbar-toggler span {
      @include font_color;
    }

    .header-list {
      position: absolute !important;
      top: 100% !important;
      right: 10px !important;
      @include bg_color;
      @include font_color;
      padding: 0.5rem 1rem !important;
      border-radius: 0 0 8px 8px !important;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3) !important;
      z-index: 99999 !important;
      width: 120px !important;
      flex-direction: column !important;
      gap: 0.5rem !important;
      box-sizing: border-box !important;
    }

    // 修复：移除按钮点击后的焦点边框
    button:focus, button:active {
      outline: none !important;
      box-shadow: none !important;
    }

    // 小屏隐藏大屏弹窗（小屏无需hover弹窗）
    .header-user-menu .user-avatar-wrapper .user-avatar-popup {
      display: none !important;
    }
  }

  @media (min-width: 992px) {
    .mobile-login-btn, .nav-middle {
      display: none !important;
    }

    .header-list {
      display: flex !important;
      gap: 1.5rem !important;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}
.me-1 {
  margin-right: 0.25rem !important;
}
.me-2 {
  margin-right: 0.5rem !important;
}

// 全局修复：移除所有按钮的焦点边框
:deep(button) {
  &:focus, &:active {
    outline: none !important;
    box-shadow: none !important;
  }
}

// 辅助样式（弹窗内边距、边框）
:deep(.border-b) {
  border-bottom: 1px solid #f0e6f8 !important; // 适配#faf3fc的边框色
}
:deep(.border-gray-200) {
  border-color: #f0e6f8 !important;
}
:deep(.text-sm) {
  font-size: 12px !important;
}
:deep(.text-gray-500) {
  color: #666666 !important;
}
:deep(.fw-bold) {
  font-weight: 600 !important;
}
:deep(.text-center) {
  text-align: center !important;
}
:deep(.flex-column) {
  flex-direction: column !important;
}
:deep(.align-items-center) {
  align-items: center !important;
}
:deep(.justify-content-center) {
  justify-content: center !important;
}
</style>
